import React, { FC, useMemo } from 'react';

import { RenderElementProps } from 'slate-react';

// components
import AampeTooltip from '../../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';
// constants
import ICONS from '../../../../constants/icons';

// models
import { ButtonElement, ButtonType } from '../../../../models/editor.model';

interface ButtonElementMainProps extends RenderElementProps {
    element: ButtonElement;
    handleRemoveButton: (buttonType: ButtonType) => void;
    formulaHasSecondaryButton: boolean;
    label?: string;
}

interface StyledProps {
    isDisabled: boolean;
}

const ButtonElementMain: FC<React.PropsWithChildren<ButtonElementMainProps>> =
    ({
        attributes,
        children,
        element,
        handleRemoveButton,
        formulaHasSecondaryButton,
        label,
    }) => {
        const canDeleteButton = useMemo<boolean>(
            () =>
                !formulaHasSecondaryButton ||
                element.buttonType === ButtonType.secondary,
            [formulaHasSecondaryButton, element.buttonType],
        );

        return (
            <ButtonContainer {...attributes}>
                <ButtonLabel contentEditable={false}>
                    {element.buttonType} {label ? label : 'button'}
                </ButtonLabel>
                <ButtonElementSection>
                    {children}
                    <AampeTooltip
                        title={
                            canDeleteButton
                                ? ''
                                : 'Primary button is required if secondary button is present.'
                        }
                    >
                        <DeleteIcon
                            contentEditable={false}
                            onClick={() =>
                                canDeleteButton
                                    ? handleRemoveButton(element.buttonType)
                                    : null
                            }
                            isDisabled={!canDeleteButton}
                        >
                            <ICONS.trash />
                        </DeleteIcon>
                    </AampeTooltip>
                </ButtonElementSection>
            </ButtonContainer>
        );
    };

const ButtonContainer = styled.div({
    marginTop: aampeTheme.spacing(2),
});

const ButtonLabel = styled.p({
    paddingLeft: aampeTheme.spacing(0.5),
    textTransform: 'capitalize',
    fontWeight: 'bold',
    color: aampeTheme.colors.gray600,
    userSelect: 'none',
});

const ButtonElementSection = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    marginTop: aampeTheme.spacing(0.5),
    alignItems: 'center',
});

const DeleteIcon = styled.div.withConfig({
    shouldForwardProp: (prop) => !['isDisabled'].includes(prop), // Prevent forwarding 'isDisabled' prop
})<StyledProps>(({ isDisabled }) => ({
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    color: isDisabled
        ? aampeTheme.colors.bordersWG01
        : aampeTheme.colors.textAccessibleWG01,
    '&:hover': {
        color: isDisabled
            ? aampeTheme.colors.bordersWG01
            : aampeTheme.colors.error500,
    },
}));

export default ButtonElementMain;
